import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobWorkBalance() {
  const [data, setData] = useState({ summary: {}, vendors: [] });
  const [loading, setLoading] = useState(true);
  const [expandedVendors, setExpandedVendors] = useState({});
  const [filterVendor, setFilterVendor] = useState('');
  const [showOnlyWithData, setShowOnlyWithData] = useState(true);

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/jobwork/balance`);
      setData(response.data);
      // Auto-expand vendors with data
      const expanded = {};
      response.data.vendors?.forEach(v => {
        if (v.summary.total_pending_schedule > 0 || v.summary.total_material_at_vendor > 0) {
          expanded[v.vendor_id] = true;
        }
      });
      setExpandedVendors(expanded);
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVendor = (vendorId) => {
    setExpandedVendors(prev => ({
      ...prev,
      [vendorId]: !prev[vendorId]
    }));
  };

  // Filter vendors
  const filteredVendors = (data.vendors || []).filter(vendor => {
    if (showOnlyWithData && vendor.summary.total_pending_schedule === 0 && vendor.summary.total_material_at_vendor === 0) {
      return false;
    }
    if (filterVendor && !vendor.vendor_name.toLowerCase().includes(filterVendor.toLowerCase())) {
      return false;
    }
    return true;
  });

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Job Work Balance</h1>
        <p className="text-sm text-gray-500 mt-1">Vendor-wise summary of pending schedule and material balance</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-blue-500">
          <p className="text-sm text-gray-600">Total Vendors</p>
          <p className="text-3xl font-bold text-blue-600">
            {data.summary.total_vendors || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-amber-500">
          <p className="text-sm text-gray-600">Total Pending Schedule</p>
          <p className="text-3xl font-bold text-amber-600">
            {(data.summary.total_pending_schedule || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-gray-500">{data.summary.vendors_with_pending || 0} vendors</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-orange-500">
          <p className="text-sm text-gray-600">Material at Vendors</p>
          <p className="text-3xl font-bold text-orange-600">
            {(data.summary.total_material_at_vendors || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-gray-500">{data.summary.vendors_with_material || 0} vendors</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-purple-500">
          <p className="text-sm text-gray-600">Total Outstanding</p>
          <p className="text-3xl font-bold text-purple-600">
            {((data.summary.total_pending_schedule || 0) + (data.summary.total_material_at_vendors || 0)).toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Search Vendor</label>
            <input
              type="text"
              placeholder="Vendor name..."
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
            />
          </div>
          <div className="flex items-center gap-2 mt-5">
            <input
              type="checkbox"
              id="showOnlyWithData"
              checked={showOnlyWithData}
              onChange={(e) => setShowOnlyWithData(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="showOnlyWithData" className="text-sm text-gray-700">
              Show only vendors with pending items
            </label>
          </div>
          <div className="ml-auto mt-5 text-sm text-gray-500">
            Showing {filteredVendors.length} of {data.vendors?.length || 0} vendors
          </div>
        </div>
      </div>

      {/* Vendor Cards */}
      {filteredVendors.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No vendors with pending data found.
        </div>
      ) : (
        <div className="space-y-4">
          {filteredVendors.map(vendor => (
            <div key={vendor.vendor_id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Vendor Header - Clickable */}
              <div 
                className="p-4 border-b border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleVendor(vendor.vendor_id)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {expandedVendors[vendor.vendor_id] ? '▼' : '▶'}
                    </span>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{vendor.vendor_name}</h2>
                      <div className="flex gap-4 text-sm text-gray-500">
                        <span>{vendor.summary.total_po_items} PO items</span>
                        <span>{vendor.summary.total_challan_parts} Challan parts</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <p className="text-xs text-gray-500">Pending Schedule</p>
                      <p className={`text-xl font-bold ${vendor.summary.total_pending_schedule > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {vendor.summary.total_pending_schedule.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Material Balance</p>
                      <p className={`text-xl font-bold ${vendor.summary.total_material_at_vendor > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                        {vendor.summary.total_material_at_vendor.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedVendors[vendor.vendor_id] && (
                <div className="p-4 space-y-6">
                  {/* Section 1: Pending Schedule (from POs) */}
                  {vendor.pending_schedule?.length > 0 && (
                    <div>
                      <h3 className="text-md font-semibold text-amber-700 mb-3 flex items-center gap-2">
                        <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                        Pending Schedule (from POs)
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-amber-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">PO Number</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Part Code</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Description</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Type</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Ordered</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Received</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase bg-amber-100">Pending</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {vendor.pending_schedule.map((item, idx) => (
                              <tr key={idx} className={item.pending > 0 ? 'bg-amber-50' : ''}>
                                <td className="px-4 py-2 text-gray-600">{item.po_number}</td>
                                <td className="px-4 py-2 font-medium text-gray-900">{item.partcode}</td>
                                <td className="px-4 py-2 text-gray-600">{item.description}</td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    item.item_type === 'Job Work' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {item.item_type}
                                  </span>
                                  {item.job_work_type && (
                                    <span className="ml-1 text-xs text-gray-500">({item.job_work_type})</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-700">{item.ordered.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2 text-right text-green-600">{item.received.toLocaleString('en-IN')}</td>
                                <td className={`px-4 py-2 text-right font-bold bg-amber-100 ${item.pending > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                                  {item.pending.toLocaleString('en-IN')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-amber-100">
                            <tr>
                              <td colSpan="4" className="px-4 py-2 font-semibold text-gray-700">Total</td>
                              <td className="px-4 py-2 text-right font-bold text-gray-700">
                                {vendor.pending_schedule.reduce((sum, i) => sum + i.ordered, 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-green-600">
                                {vendor.pending_schedule.reduce((sum, i) => sum + i.received, 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-amber-700">
                                {vendor.pending_schedule.reduce((sum, i) => sum + i.pending, 0).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Section 2: Material Balance (from Challans) */}
                  {vendor.material_balance?.length > 0 && (
                    <div>
                      <h3 className="text-md font-semibold text-orange-700 mb-3 flex items-center gap-2">
                        <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                        Material Balance (from Challans)
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-orange-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Part Code</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Description</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Sent (Fresh)</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Sent (Rework)</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase bg-blue-50">Total Sent</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Received</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase bg-orange-100">Balance</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {vendor.material_balance.map((item, idx) => (
                              <tr key={idx} className={item.balance > 0 ? 'bg-orange-50' : ''}>
                                <td className="px-4 py-2 font-medium text-gray-900">{item.partcode}</td>
                                <td className="px-4 py-2 text-gray-600">{item.description}</td>
                                <td className="px-4 py-2 text-right text-gray-700">
                                  {item.sent_fresh > 0 ? item.sent_fresh.toLocaleString('en-IN') : '-'}
                                </td>
                                <td className="px-4 py-2 text-right text-amber-600">
                                  {item.sent_rework > 0 ? item.sent_rework.toLocaleString('en-IN') : '-'}
                                </td>
                                <td className="px-4 py-2 text-right font-semibold text-blue-700 bg-blue-50">
                                  {item.total_sent.toLocaleString('en-IN')}
                                </td>
                                <td className="px-4 py-2 text-right text-green-600">
                                  {item.received.toLocaleString('en-IN')}
                                </td>
                                <td className={`px-4 py-2 text-right font-bold bg-orange-100 ${item.balance > 0 ? 'text-orange-700' : 'text-green-600'}`}>
                                  {item.balance.toLocaleString('en-IN')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-orange-100">
                            <tr>
                              <td colSpan="2" className="px-4 py-2 font-semibold text-gray-700">Total</td>
                              <td className="px-4 py-2 text-right font-bold text-gray-700">
                                {vendor.material_balance.reduce((sum, i) => sum + i.sent_fresh, 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-amber-600">
                                {vendor.material_balance.reduce((sum, i) => sum + i.sent_rework, 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-blue-700">
                                {vendor.material_balance.reduce((sum, i) => sum + i.total_sent, 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-green-600">
                                {vendor.material_balance.reduce((sum, i) => sum + i.received, 0).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-orange-700">
                                {vendor.material_balance.reduce((sum, i) => sum + i.balance, 0).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* No data message */}
                  {(!vendor.pending_schedule || vendor.pending_schedule.length === 0) && 
                   (!vendor.material_balance || vendor.material_balance.length === 0) && (
                    <div className="text-center text-gray-500 py-4">
                      No pending items for this vendor.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-900 mb-2">Understanding the Balance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div className="space-y-1">
            <p className="font-medium text-amber-700">📋 Pending Schedule (from POs)</p>
            <p>Items ordered via Purchase Orders but not yet received.</p>
            <p><strong>Pending = Ordered - Received</strong></p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-orange-700">📦 Material Balance (from Challans)</p>
            <p>Physical material sent to vendor via Challans for processing.</p>
            <p><strong>Balance = Sent - Received</strong> (items currently at vendor)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
